import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Download, Search, Trash2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Avatar, Loading, EmptyState } from "../components/ui";

export default function Employees() {
  const { user, meta } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  const [page, setPage] = useState(1);
  const canManage = ["SUPERADMIN", "HR_ADMIN"].includes(user?.role);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page, limit: 12 });
    if (q) params.set("q", q);
    if (dept) params.set("department", dept);
    const { data } = await api.get(`/employees?${params}`);
    setData(data);
  }, [q, dept, page]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const exportCsv = async () => {
    const res = await api.get("/employees/export/csv", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "employees.csv"; a.click();
  };

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this employee?")) return;
    await api.delete(`/employees/${id}`); load();
  };

  const pages = data ? Math.ceil(data.total / data.limit) : 1;

  return (
    <div>
      <PageHeader title="Employees" subtitle={data ? `${data.total} people in the organisation` : ""}>
        {canManage && <button data-testid="export-csv" onClick={exportCsv} className="btn-outline"><Download size={15} /> Export</button>}
        {canManage && <Link to="/employees/new" data-testid="add-employee" className="btn-accent"><Plus size={16} /> Add Employee</Link>}
      </PageHeader>

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input data-testid="employee-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search by name, code or email…" className="input pl-9" />
        </div>
        <select data-testid="employee-dept-filter" value={dept} onChange={(e) => { setDept(e.target.value); setPage(1); }} className="input w-auto min-w-[180px]">
          <option value="">All departments</option>
          {meta?.departments?.map((dp) => <option key={dp.id} value={dp.id}>{dp.name}</option>)}
        </select>
      </div>

      {!data ? <Loading /> : data.items.length === 0 ? <EmptyState icon={Search} title="No employees found" hint="Try a different search." /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="employee-table">
              <thead><tr>
                <th className="th">Employee</th><th className="th">Code</th><th className="th">Department</th>
                <th className="th">Designation</th><th className="th">Location</th><th className="th">Status</th>
                {canManage && <th className="th"></th>}
              </tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {data.items.map((e) => (
                  <tr key={e.id} onClick={() => nav(`/employees/${e.id}`)} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`employee-row-${e.employeeCode}`}>
                    <td className="td"><div className="flex items-center gap-3"><Avatar name={e.fullName} size={34} url={e.profilePhotoUrl} /><div><div className="font-medium text-slate-800 dark:text-slate-100">{e.fullName}</div><div className="text-xs text-slate-400">{e.email}</div></div></div></td>
                    <td className="td font-mono text-xs">{e.employeeCode}</td>
                    <td className="td">{e.departmentName || "—"}</td>
                    <td className="td">{e.designationName || "—"}</td>
                    <td className="td">{e.locationName || "—"}</td>
                    <td className="td"><Badge value={e.status} /></td>
                    {canManage && <td className="td"><button onClick={(ev) => del(e.id, ev)} className="text-slate-400 hover:text-red-500" data-testid={`delete-${e.employeeCode}`}><Trash2 size={15} /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {data && pages > 1 && (
        <div className="mt-5 flex items-center justify-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="btn-outline">Prev</button>
          <span className="text-sm text-slate-500">Page {page} of {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="btn-outline">Next</button>
        </div>
      )}
    </div>
  );
}
